import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { Button, DateInput, Modal, TimeInput } from '@/components/ui'
import type { Attendance } from '@/features/attendance/types/attendance.types'
import { useUpdateAttendance } from '@/features/attendance/hooks/useAttendance'

interface EditAttendanceTimesModalProps {
  companyId: string
  attendance: Attendance
  open: boolean
  onClose: () => void
}

function splitIso(iso?: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' }
  const d = parseISO(iso)
  return { date: format(d, 'yyyy-MM-dd'), time: format(d, 'HH:mm') }
}

function joinLocal(date: string, time: string): Date | null {
  if (!date || !time) return null
  const d = new Date(`${date}T${time}:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Direct clock-in / clock-out edit for managers, HR and admins holding
 * `attendance:update`. Times are entered in the viewer's local time zone.
 */
export function EditAttendanceTimesModal({
  companyId,
  attendance,
  open,
  onClose,
}: EditAttendanceTimesModalProps): React.ReactElement | null {
  const initialIn = splitIso(attendance.clockIn)
  const initialOut = splitIso(attendance.clockOut)
  const [inDate, setInDate] = useState(initialIn.date)
  const [inTime, setInTime] = useState(initialIn.time)
  const [outDate, setOutDate] = useState(initialOut.date || initialIn.date)
  const [outTime, setOutTime] = useState(initialOut.time)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const update = useUpdateAttendance()

  if (!open) return null

  const handleSave = (): void => {
    const clockIn = joinLocal(inDate, inTime)
    const clockOut = joinLocal(outDate, outTime)
    if (!clockIn || !clockOut) {
      setError('Enter both clock-in and clock-out.')
      return
    }
    if (clockOut <= clockIn) {
      setError('Clock-out must be after clock-in.')
      return
    }
    setError(null)
    update.mutate(
      {
        companyId,
        id: attendance._id,
        body: {
          clockIn: clockIn.toISOString(),
          clockOut: clockOut.toISOString(),
          note: note.trim() || undefined,
        },
      },
      { onSuccess: onClose }
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit attendance"
      className="max-w-md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleSave} loading={update.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:bg-blue-950/30 dark:text-blue-200">
          Worked time, lateness and deductions are recalculated. The record is marked as edited
          and the change is audited.
        </p>
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,7rem)] items-center gap-2 sm:grid-cols-[minmax(0,5rem)_minmax(0,1fr)_minmax(0,7rem)]">
            <label htmlFor="attendance-edit-in-time" className="col-span-2 text-sm font-medium text-gray-700 sm:col-span-1 dark:text-gray-300">
              Clock-in
            </label>
            <DateInput
              value={inDate}
              onChange={(e) => setInDate(e.target.value)}
              aria-label="Clock-in date"
            />
            <TimeInput
              value={inTime}
              onChange={setInTime}
              id="attendance-edit-in-time"
              placeholder="HH:mm"
            />
            <label htmlFor="attendance-edit-out-time" className="col-span-2 text-sm font-medium text-gray-700 sm:col-span-1 dark:text-gray-300">
              Clock-out
            </label>
            <DateInput
              value={outDate}
              onChange={(e) => setOutDate(e.target.value)}
              aria-label="Clock-out date"
            />
            <TimeInput
              value={outTime}
              onChange={setOutTime}
              id="attendance-edit-out-time"
              placeholder="HH:mm"
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="attendance-edit-note"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Note (optional)
          </label>
          <textarea
            id="attendance-edit-note"
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Left at 17:00, confirmed by line manager"
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </Modal>
  )
}
